import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Observable, catchError } from 'rxjs';
import { map } from 'rxjs/operators';
import type {
    GroupCreateRequestDto,
    GroupForm,
    GroupTileAssignmentRequestDto,
    GroupUserAssignmentRequestDto,
    UserGroupAssignmentRequestDto,
} from '@/core/models/group-form';

@Injectable({ providedIn: 'root' })
export class GroupService extends ApiService {
    public createGroup(groupDetails: GroupCreateRequestDto): Observable<GroupForm> {
        return this._http.post<GroupForm>(ApiService.openApiUrl + 'groups', groupDetails).pipe(
            map((response) => {
                // Process the response if necessary
                return response;
            }),
            catchError((error) => {
                console.error('Error creating group:', error);
                throw error;
            }),
        );
    }

    public deleteGroup(groupId: number): Observable<GroupForm> {
        return this._http.delete<GroupForm>(ApiService.openApiUrl + 'groups/' + groupId).pipe(
            map((response) => {
                return response;
            }),
            catchError((error) => {
                console.error('Error deleting group:', error);
                throw error;
            }),
        );
    }

    /**
     * Assigns a user to multiple groups.
     * @param userId The ID of the user to assign.
     * @param groupIds The IDs of the groups to assign the user to.
     * @returns An Observable that resolves to the updated groups.
     */
    public updateUserGroupAssignments(userId: number, groupIds: number[]): Observable<GroupForm[]> {
        const request = {
            groupIds,
        } satisfies UserGroupAssignmentRequestDto;
        return this._http.put<GroupForm[]>(ApiService.openApiUrl + 'groups/users/' + userId + '/groups', request).pipe(
            map((response) => {
                // Process the response if necessary
                return response;
            }),
            catchError((error) => {
                console.error('Error assigning user to groups:', error);
                throw error;
            }),
        );
    }

    public assignTilesToGroup(groupId: number, tileIds: number[]): Observable<GroupForm> {
        const request = {
            tileIds,
        } satisfies GroupTileAssignmentRequestDto;
        return this._http.put<GroupForm>(ApiService.openApiUrl + 'groups/' + groupId + '/tiles', request).pipe(
            map((response) => {
                // Process the response if necessary
                return response;
            }),
            catchError((error) => {
                console.error('Error assigning tiles to group:', error);
                throw error;
            }),
        );
    }

    public assignUsersToGroup(groupId: number, userIds: number[]): Observable<GroupForm> {
        const request = { userIds } satisfies GroupUserAssignmentRequestDto;
        return this._http.put<GroupForm>(ApiService.openApiUrl + 'groups/' + groupId + '/users', request).pipe(
            map((response) => {
                return response;
            }),
            catchError((error) => {
                console.error('Error assigning users to group:', error);
                throw error;
            }),
        );
    }

    public changeGroupName(groupId: number, newName: string): Observable<GroupForm> {
        return this._http
            .patch<GroupForm>(ApiService.openApiUrl + 'groups/' + groupId + '/name', {
                newGroupName: newName,
            })
            .pipe(
                map((response) => {
                    return response;
                }),
                catchError((error) => {
                    console.error('Error changing group name:', error);
                    throw error;
                }),
            );
    }

    public getAllGroups(): Observable<GroupForm[]> {
        return this._http.get<GroupForm[]>(ApiService.openApiUrl + 'groups').pipe(
            map((response: GroupForm[]) => response), // This map operation is technically optional as it's a direct pass-through
        );
    }
}
